import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { WindowService } from 'src/shared/providers/window.service';
import { RentalModesService } from 'src/shared/services/rental-modes.service';

import { FinanceTab } from 'src/models/finance.model';

@Component({
  templateUrl: 'finance.page.html',
  styleUrls: ['./finance.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FinancePage implements OnInit {
  readonly windowService = inject(WindowService);

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly rentalModes = inject(RentalModesService);

  loaded = false;
  selectedTab: FinanceTab = 'bills';

  // Tabs that only exist for long-term rentals — dropped when Account Settings
  // (My Account) turns the long-term toggle off.
  private static readonly LONG_TERM_TABS: FinanceTab[] = ['deposits', 'rent-collection'];

  private readonly allTabs: { value: FinanceTab; label: string; icon?: string }[] = [
    { value: 'bills', label: 'Bills', icon: 'assets/svg/receipt-outline.svg' },
    // { value: 'receivables', label: 'Receivables', icon: 'assets/svg/cash-outline.svg' },
    { value: 'deposits', label: 'Deposits', icon: 'assets/svg/wallet-outline.svg' },
    { value: 'rent-collection', label: 'Rent Collection', icon: 'assets/svg/card-outline.svg' },
    { value: 'leases', label: 'Leases', icon: 'assets/svg/document-text-outline.svg' },
    // { value: 'income', label: 'Income', icon: 'assets/svg/trending-up-outline.svg' },
    { value: 'transactions', label: 'Transactions', icon: 'assets/svg/swap-horizontal-outline.svg' },
    { value: 'taxes', label: 'Taxes', icon: 'assets/svg/calculator-outline.svg' },
    { value: 'monthly', label: 'Monthly P&L', icon: 'assets/svg/grid-outline.svg' },
  ];

  tabs = this.allTabs;

  get tabTitle(): string {
    return this.tabs.find(t => t.value === this.selectedTab)?.label ?? 'Finance';
  }

  ngOnInit() {
    this.loaded = true;
    // A section with its own address (Rent Collection) shows only that section.
    const pageTab: FinanceTab | undefined = this.route.snapshot.data['tab'];
    if (pageTab) {
      this.selectedTab = pageTab;
    }

    this.rentalModes.modes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(modes => {
        this.tabs = modes.longTerm
          ? this.allTabs
          : this.allTabs.filter(t => !FinancePage.LONG_TERM_TABS.includes(t.value));
        // Turning the toggle off while sitting on a now-hidden tab would leave the
        // page showing content with no matching tab — fall back to the first one.
        if (!this.tabs.some(t => t.value === this.selectedTab)) {
          // On a hidden section's own page (Rent Collection), go to Finance itself
          // rather than show its first section under that address.
          if (pageTab) {
            this.router.navigate(['/finance'], { replaceUrl: true });
            return;
          }
          this.selectedTab = this.tabs[0].value;
        }
        this.cdr.markForCheck();
      });

    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        if (!pageTab && params['tab'] && this.tabs.some(t => t.value === params['tab'])) {
          this.selectedTab = params['tab'] as FinanceTab;
          // OnPush: the query-param change must be flushed to the view.
          this.cdr.markForCheck();
        }
      });
  }

  selectTab(tab: string) {
    this.selectedTab = tab as FinanceTab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
